import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, Star, Phone, X, Loader2, Building2, ShieldCheck, CheckCircle2, Send } from "lucide-react";

/**
 * Modal showing nearby cashless garages + one-tap quote-request handoff.
 * Required props: onClose. Optional: pincode, city, insurer, speciality, claimId, docIds, customerMobile, customerName.
 */
export default function NearbyGarages({
  pincode, city, insurer, speciality,
  claimId, docIds = [], customerMobile = "", customerName = "",
  onClose,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeQuoteFor, setActiveQuoteFor] = useState(null);     // workshop id whose quote form is open
  const [mobile, setMobile] = useState(customerMobile);
  const [name, setName] = useState(customerName);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState({});  // workshop_id -> {success: {...}, error}

  const load = async (p, c) => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (p) params.pincode = p;
      if (c) params.city = c;
      if (insurer) params.insurer = insurer;
      if (speciality) params.speciality = speciality;
      params.limit = 5;
      const { data } = await api.get("/partners/nearby", { params });
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load nearby garages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pincode || city) load(pincode, city);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pincode, city, insurer, speciality]);

  const submitQuote = async (workshop) => {
    if (!mobile || mobile.length < 10) {
      setResults((r) => ({ ...r, [workshop.id]: { error: "Please enter a valid 10-digit mobile" } }));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/partners/quote-request", {
        workshop_id: workshop.id,
        mobile,
        name: name || null,
        claim_id: claimId || null,
        doc_ids: docIds,
        notes: insurer ? `Insurer: ${insurer}` : null,
      });
      setResults((r) => ({ ...r, [workshop.id]: { success: data } }));
      setActiveQuoteFor(null);
    } catch (e) {
      setResults((r) => ({ ...r, [workshop.id]: { error: e.response?.data?.detail || "Could not send request" } }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm fade-in-up" data-testid="nearby-garages-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-[#003DA5] to-[#0050D1] text-white">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">Nearby Network</div>
            <h2 className="font-heading font-bold text-lg">Cashless garages near you</h2>
            {(pincode || city) && (
              <div className="text-xs opacity-90 mt-0.5">
                <MapPin className="inline w-3 h-3 mr-0.5" /> {pincode ? `Pincode ${pincode}` : city}
                {insurer && <> · {insurer}</>}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" data-testid="nearby-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
          {loading && (
            <div className="flex items-center gap-3 text-slate-600 py-8 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" /> Finding garages within reach…
            </div>
          )}
          {error && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="nearby-error">{error}</div>
          )}
          {!loading && !error && data && data.workshops.length === 0 && (
            <div className="text-center py-10" data-testid="nearby-empty">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="font-semibold text-slate-700">No partner garages found nearby</p>
              <a href="tel:+919942249388" className="mt-3 inline-flex items-center gap-2 bg-[#003DA5] text-white rounded-lg px-4 py-2 text-sm font-semibold">
                <Phone className="w-4 h-4" /> +91 99422 49388
              </a>
            </div>
          )}
          {!loading && data && data.workshops.length > 0 && (
            <ul className="space-y-3" data-testid="nearby-list">
              {data.workshops.map((w) => {
                const result = results[w.id];
                const formOpen = activeQuoteFor === w.id;
                return (
                  <li key={w.id} className="bg-white rounded-xl border border-slate-200 p-4 hover-lift" data-testid={`workshop-${w.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-heading font-bold text-slate-900 text-base truncate">{w.name}</h3>
                          {w.cashless && (
                            <span className="px-2 py-0.5 rounded-full bg-[#E6F9F0] text-[#00A86B] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 shrink-0">
                              <ShieldCheck className="w-3 h-3" /> Cashless
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">{w.address} · {w.city} {w.pincode}</p>
                        <div className="mt-2 flex items-center gap-3 text-xs">
                          <span className="flex items-center gap-1 text-slate-700 font-semibold">
                            <Star className="w-3.5 h-3.5 fill-[#FF7A00] text-[#FF7A00]" /> {w.rating}
                          </span>
                          {w.distance_km != null && (
                            <span className="flex items-center gap-1 text-slate-600">
                              <MapPin className="w-3.5 h-3.5" /> {w.distance_km} km
                            </span>
                          )}
                          <span className="text-slate-500 truncate">{w.speciality?.slice(0, 3).join(" · ")}</span>
                        </div>
                      </div>
                    </div>

                    {result?.success ? (
                      <div className="mt-3 p-3 rounded-lg bg-[#E6F9F0] border border-[#00A86B]/30 text-sm" data-testid={`quote-success-${w.id}`}>
                        <div className="flex items-center gap-2 font-bold text-[#006B45]">
                          <CheckCircle2 className="w-4 h-4" /> Quote request sent
                        </div>
                        <p className="text-xs text-[#006B45] mt-1">{result.success.message}</p>
                        <p className="text-[10px] text-slate-500 mt-1">Ref: <span className="font-mono font-bold">{result.success.quote_request_id}</span></p>
                      </div>
                    ) : formOpen ? (
                      <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                          Send photos & request quote
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2 mb-2">
                          <input
                            type="text" value={name} onChange={(e) => setName(e.target.value)}
                            placeholder="Your name (optional)"
                            className="bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#003DA5] outline-none"
                            data-testid={`quote-name-${w.id}`}
                          />
                          <input
                            type="tel" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                            maxLength={10} placeholder="10-digit mobile *"
                            className="bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#003DA5] outline-none"
                            data-testid={`quote-mobile-${w.id}`}
                          />
                        </div>
                        {result?.error && <div className="text-xs text-red-600 mb-2" data-testid={`quote-error-${w.id}`}>{result.error}</div>}
                        <div className="flex gap-2">
                          <button
                            onClick={() => submitQuote(w)}
                            disabled={submitting || mobile.length < 10}
                            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#003DA5] hover:bg-[#002F80] disabled:bg-slate-300 text-white rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors"
                            data-testid={`quote-send-${w.id}`}
                          >
                            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            {submitting ? "Sending…" : `Send ${docIds.length > 0 ? `${docIds.length} photo${docIds.length > 1 ? "s" : ""} & ` : ""}quote request`}
                          </button>
                          <button
                            onClick={() => setActiveQuoteFor(null)}
                            className="text-xs font-semibold text-slate-500 hover:text-slate-800 px-3"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => setActiveQuoteFor(w.id)}
                          className="flex-1 bg-[#003DA5] hover:bg-[#002F80] text-white rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wider"
                          data-testid={`workshop-send-${w.id}`}
                        >
                          Send {docIds.length > 0 ? `${docIds.length} photos & ` : ""}quote request
                        </button>
                        <a
                          href={`tel:+919942249388`}
                          className="bg-white border border-slate-200 hover:border-[#003DA5] text-slate-700 rounded-lg px-3 py-2 text-xs font-bold flex items-center gap-1"
                          data-testid={`workshop-call-${w.id}`}
                        >
                          <Phone className="w-3 h-3" /> Call
                        </a>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-6 py-3 bg-white border-t border-slate-100 text-[10px] text-slate-500">
          Showing top {data?.count ?? 0} matches · The workshop will call you within 2 hours after your request.
        </div>
      </div>
    </div>
  );
}
